
import React, { useState, useEffect, useMemo, useRef } from 'react'
import { EnumType } from 'json-to-graphql-query';

import classNames from 'classnames'
import Card from 'components/Detail/Card'
import SocketHandler from 'components/SocketHandler'
import { VuiMarkdown } from '@topkamera/viso-ui'
import { chartDotMenu } from './utils'
import { defaultNotifier } from 'functions/notificationHandler'
import { handleMutation } from '../../../../../utils/request'
import { cancelPreviewInsightRequests, generateInsightData, previewInsight } from '../../../../../api/ai-agents'
import { generateInsightChartPayload } from '../editor/utils'
import {hasWriteAccess, isReadOnlyPermission, getPermission} from '../../../../../utils/permissions'
import { VuiButton, VuiLoader } from '@topkamera/viso-ui'

const AiInsightChart = ({
  color, chart, isEditMode, isPreview, height, onToogleMenu, onEdit, onCopy, onDelete, onExplainKPI, onImproveChart, showAI, data = {}, deviceIds = [], allDevices = [], dashboardTimeRange = {}, dashboardId, chartValues, refreshTick = 0, detailRole, hasPermissionToAccessAiInsight
}) => {
  const { id, title, text, updatedAt, status } = data
  const displayTitle = isPreview ? (chartValues?.chartTitle || title || 'AI Insights') : (title || 'AI Insights')
  const [isChartDataLoading, setIsChartDataLoading] = useState(false)
  const [chartData, setChartData] = useState(null)
  const [previewData, setPreviewData] = useState(null)
  const prevDepsRef = useRef()
  const previewRequestIdRef = useRef(0)

  const aiInsightPermission = detailRole?.role && getPermission('permissionAiInsights', detailRole.role)

  const isReadOnlyLocations = aiInsightPermission && isReadOnlyPermission(aiInsightPermission)
  const aiInsightEnabled = hasPermissionToAccessAiInsight && hasWriteAccess(aiInsightPermission)

  useEffect(() => {
    if (status === 'processing') {
      setIsChartDataLoading(true)
    } else {
      setIsChartDataLoading(false)
    }
  }, [status])

  const normalizeDeviceNames = (items = []) => {
    return items
      .map(device => {
        if (typeof device === 'object') {
          return device?.name || device?.label || device?.deviceName || device?.value
        }

        return device
      })
      .filter(deviceName => !!deviceName && deviceName !== 'Dashboard Default')
  }

  const allDeviceNames = useMemo(() => normalizeDeviceNames(allDevices || []), [allDevices])
  const selectedDeviceNames = useMemo(() => normalizeDeviceNames(deviceIds || []), [deviceIds])

  const devices = useMemo(() => {
    if (isPreview) {
      return allDeviceNames
    }

    return selectedDeviceNames.length > 0 ? selectedDeviceNames : allDeviceNames
  }, [isPreview, allDeviceNames, selectedDeviceNames])

  // This prevents re-fetching when UI-only state (like expandedLabel) changes (Expand briefing data)
  const relevantData = useMemo(() => {
    if (!data) return null
    return {
      mode: data.mode,
      dataSource: data.dataSource,
      title: data.title,
      chartsToInclude: data.chartsToInclude,
      briefing: data.briefing,
      keyFinding: data.keyFinding
    }
  }, [data?.mode, data?.dataSource, data?.title, data?.chartsToInclude, data?.briefing, data?.keyFinding])

  const fetchPreviewInsight = async () => {
    if (!isPreview) {
      return
    }

    // Only fetch if mode and dataSource are selected
    if (!data?.mode || data?.mode?.length === 0 || !data?.dataSource || data?.dataSource?.length === 0) {
      return
    }

    const insightChart = generateInsightChartPayload({
        insightsData: data,
        chartTitle: data.title,
        dashboardId: dashboardId
      })
        
    delete insightChart.title;

    const payload = {
      model: new EnumType('gpt54'),
      visoDevices: devices,
      timeRange: {
        start: dashboardTimeRange?.start,
        end: dashboardTimeRange?.end
      },
      dashboardId: dashboardId,
      ...insightChart
    }

    // Cancel any in-flight/queued preview requests so the latest payload wins
    cancelPreviewInsightRequests();

    const requestId = ++previewRequestIdRef.current
    setIsChartDataLoading(true)
    try {
      const response = await previewInsight(payload)
      if (requestId !== previewRequestIdRef.current) return

      if(response.data.errors) {
        return defaultNotifier(response.data.errors)
      }
      if (response?.data?.data?.previewInsight) {
        setPreviewData({ text: response.data.data.previewInsight })
      }
    } catch (error) {
      if (requestId !== previewRequestIdRef.current) return
      const msg = (error && (error.message || String(error))) || '';
      const cancelled =
        msg === 'PREVIEW_INSIGHT_QUEUE_CANCELLED' ||
        msg === 'CanceledError' ||
        msg === 'canceled' ||
        msg === 'cancelled' ||
        (error && error.name === 'CanceledError') ||
        (error && error.code === 'ERR_CANCELED') ||
        (error && error.name === 'AbortError');
      if (!cancelled) {
        defaultNotifier(error)
      }
    } finally {
      if (requestId === previewRequestIdRef.current) {
        setIsChartDataLoading(false)
      }
    }
  }

  const onRefresh = async () => {
    if (isPreview) {
      await fetchPreviewInsight()
      return
    }

    const payload = {
      model: new EnumType('gpt54'),
      id: id,
      visoDevices: devices,
      timeRange: {
        start: dashboardTimeRange?.start,
        end: dashboardTimeRange?.end
      }
    }
    setIsChartDataLoading(true)
    try {
      const response = await generateInsightData(payload)
      handleMutation(response, onSuccess, 'Request to generate insights has been sent successfully')
    } catch (error) {
      defaultNotifier(error)
    }
  }

  const updateData = (data) => {
    const updateData = {
      ...chartData,
      text: data?.text,
      status: data?.status || chartData?.status,
      updatedAt: data?.updatedAt
    }
    setChartData(updateData)
    setIsChartDataLoading(data?.status === 'processing')
  }

  useEffect(() => {
    if (!isPreview) {
      setChartData(data)
      return
    }

    // Only fetch if we have devices and valid time range
    if (!dashboardTimeRange?.start || !dashboardTimeRange?.end) {
      return
    }

    const currentDeps = {
      devices: devices?.join(','),
      timeStart: dashboardTimeRange?.start,
      timeEnd: dashboardTimeRange?.end,
      dataString: JSON.stringify(relevantData)
    }

    const prevDeps = prevDepsRef.current

    // Skip if dependencies haven't changed
    if (prevDeps && 
        prevDeps.devices === currentDeps.devices &&
        prevDeps.timeStart === currentDeps.timeStart &&
        prevDeps.timeEnd === currentDeps.timeEnd &&
        prevDeps.dataString === currentDeps.dataString) {
      return
    }

    prevDepsRef.current = currentDeps
    fetchPreviewInsight()
  }, [isPreview, devices, dashboardTimeRange?.start, dashboardTimeRange?.end, relevantData])

  useEffect(() => {
    if (isPreview && previewData) {
      setChartData(previewData)
    } else if (!isPreview) {
      setChartData(data)
    }
  }, [previewData, data, isPreview])

  useEffect(() => {
    if (!isPreview && refreshTick > 0) {
      onRefresh()
    }
  }, [refreshTick])

  useEffect(() => {
    if (isPreview) {
      return
    }

    // Only fire if we have necessary data and no text exists yet
    if (!id || !dashboardTimeRange?.start || !dashboardTimeRange?.end || !aiInsightEnabled) {
      return
    }

    onRefresh()
  }, [])

  const renderContent = () => {
    return (
      <>
        <SocketHandler
          onCallback={updateData}
          customKey='insightId'
          name={id}
        >
          <div className='job-training__title job-training__title2 job-training__title--insights'>{displayTitle}</div>
          <div className='vui-pt-28'>
            <div onMouseDown={(e) => e.stopPropagation()}>
              <VuiMarkdown
                id={id}
                color={color}
                height={height - 20}
                onRefresh={aiInsightEnabled && onRefresh}
                text={(chartData?.text || text)}
                timeStamp={aiInsightEnabled && (chartData?.updatedAt || updatedAt)}
                status={(isChartDataLoading && !(chartData?.text || text)) ? 'pending' : 'ready'}
                loaderProps={{
                  label: isPreview ? 'Loading preview insights...' : 'Generating new insights...'
                }}
              />
            </div>
          </div>
        </SocketHandler>
      </>
    )
  }

  return (
    <Card
      noGrid
      color={color}
      menu={!isPreview && 'dot'}
      chartId={chart.id}
      className={classNames(
        'overview chart-cards',
        isPreview && 'charts-card-preview'
      )}
      height={height + 70}
      content={renderContent()}
      onToogleMenu={onToogleMenu}
      dropDownMenu={!isPreview && onEdit && chartDotMenu({
        color,
        showAI: showAI,
        onEdit: onEdit,
        onCopy: onCopy,
        onDelete: onDelete,
        onExplainKPI: () => onExplainKPI(chart?.id),
        onImproveChart: () => onImproveChart(chart?.id),
        isEditMode: isEditMode && aiInsightEnabled
      })}
      customMenuAction={
        isChartDataLoading && (
          <div className="charts-card-chart__menu-action charts-card-chart__menu-action2 charts-card-chart__menu-action-display"
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
            style={{ pointerEvents: 'auto', zIndex: 999 }}>
            <VuiLoader
              label="Thinking..."
              type="dots"
            />
          </div>
        )
      }
    />
  )
}

export default AiInsightChart